import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  Box,
  Grid,
  IconButton,
  MenuItem,
} from '@mui/material';
import {PendingOutlined} from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import LockIcon from '@mui/icons-material/Lock';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import {
  Status,
  TextField,
} from '@finpay-development/shared-components';
import AccessControl from '@finpay-development/shared-platform/lib/esm/security/components/access-control';
import {RolePageNames} from '@finpay-development/shared-platform/lib/esm/security/model/role-page-names';
import {formatNumberToUSD} from '@finpay-development/shared-platform/lib/esm/shared/calculators';
import {configSettings} from '@finpay-development/shared-platform/lib/esm/shared/configuration/config-settings';
import {LoginVerifiedStatus} from '@finpay-development/shared-platform/lib/esm/shared/enums';
import {PrimaryContact} from '@finpay-development/shared-platform/lib/esm/shared/model/primary-contact';
import {Utils} from '@finpay-development/shared-platform/lib/esm/shared/utils';
import {userService} from '@finpay-development/shared-platform/lib/esm/admin/services/admin-user-service';
import {patientService} from '@finpay-development/shared-platform/lib/esm/patient/services/patient-service';
import {showErrorStatus} from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';
import {
  getClientFacilities,
} from '@finpay-development/shared-platform/lib/esm/implementation-specialist/state/facility/implementation-facility-thunk';
import {emptyPatientChampion, PatientChampion} from '@finpay-development/shared-platform/lib/esm/patient/components/models/patient-champion';
import {
  getPatientStatusByStatusId,
  StatusType,
} from '@finpay-development/shared-platform/lib/esm/patient/components/misc/patient-status';
import {
  resetPatientContext,
  setAccountHolderContext,
} from '@finpay-development/shared-platform/lib/esm/patient/state/patient-slice';
import {
  resetPatientRecordLockingContext,
  setInUseModalOpen_overview,
  setIsIOCModalOpen,
  setLockRequestedById,
  setLockStatusReleased_overview,
  setRequestDeniedModalOpen_overview,
} from '@finpay-development/shared-platform/lib/esm/patient/state/patient-record-locking-slice';
import {
  lockIOC,
  updateIOC,
  updateWarmTransfer,
} from '@finpay-development/shared-platform/lib/esm/patient/state/patient-thunk';
import {useDispatch, useSelector} from 'react-redux';
import _ from 'lodash';
import {useNavigate} from 'react-router-dom';
import CustomizeableAccordion from 'src/shared/components/customizeable-accordion';
import HeaderWithDetails from 'src/shared/components/header-with-details';
import LockModal from 'src/implementation-specialist/components/locks/lock-modal';
import AccountStatusAccordian from 'src/patient/components/patient-drilldown/account/account-status-accordian';
import {InstanceOfCareModal} from 'src/patient/components/patient-drilldown/instance-of-care/instance-of-care-modal';
import {LockablePatientRecordSection} from 'src/patient/components/models/lockable-patient-record-section';
import {
  denyRequestOk,
  editModalPoll,
  releaseRecordOk,
  requestReleaseOk,
} from 'src/patient/state/patient-record-locking-thunk';
import {RootState} from 'src/shared/state/root-reducer';
import {AppDispatch} from 'src/shared/state/store';

let editModalPollingInterval: ReturnType<typeof setInterval> | undefined;

export const IocOverviewAccordion = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const hasRunOnce = useRef(false);
  const [isIOCModalEditMode, setIsIOCModalEditMode] = useState(false);
  const [isRequestReleaseButtonDisabled, setIsRequestReleaseButtonDisabled] = useState(false);
  const [isWarmTransferCompleted, setIsWarmTransferCompleted] = useState<string>('-1');

  const countdownMax = configSettings.REACT_APP_patient_locking_countdown_max;
  const lockPollingInterval = configSettings.REACT_APP_patient_locking_polling_interval;

  const selectedPatient = useSelector(
      (state: RootState) => state.patientContext.selectedPatient,
  );
  const selectedEncounter = useSelector(
      (state: RootState) => state.patientContext.selectedEncounter,
  );
  const selectedPatientChampion = useSelector(
      (state: RootState) => state.patientContext.selectedEncounter?.patientChampion,
      (left, right) => _.isEqual(left, right),
  );
  const userName = useSelector(
      (state: RootState) => state.userContext.userProfile.userName,
  );
  const isLoadingEncounter = useSelector(
      (state: RootState) => state.patientContext.isLoading.patientIOC,
  );
  const lockingModalOpenStatus = useSelector(
      (state: RootState) => state.patientRecordLockingContext.lockingModalOpenStatus,
  );
  const isLockStatusReleasedOverview = useSelector(
      (state: RootState) => state.patientRecordLockingContext.isLockStatusReleased_overview,
  );
  const lockRequestedById = useSelector(
      (state: RootState) => state.patientRecordLockingContext.lockRequestedById,
  );
  const isIOCModalOpen = useSelector(
      (state: RootState) => state.patientRecordLockingContext.patientModalOpenStatus.isIOCModalOpen,
  );
  const accountHolderContext = useSelector(
      (state: RootState) => state.patientContext?.accountHolderContext,
  );

  const doesSelectedEncounterExist = !!selectedEncounter?.patientEncounterId;
  const doesChampionExist =
      (doesSelectedEncounterExist &&
          selectedEncounter?.patientChampion?.length > 0 &&
          selectedEncounter?.patientChampion[0] !== emptyPatientChampion) ||
      selectedEncounter?.hasChampions === false;

  const doModalPolling = useCallback((patientId: number, encounterId: number) => {
    editModalPollingInterval = setInterval(() => {
      dispatch(
          editModalPoll({
            patientId,
            encounterId,
            userName,
            lockablePatientRecordSection: LockablePatientRecordSection.overview,
          }),
      );
    }, lockPollingInterval);
  }, [dispatch, lockPollingInterval, userName]);

  const releasePatientLock = () => {
    if (selectedEncounter?.patientEncounterId) {
      setTimeout(async () => {
        await dispatch(lockIOC({lock: false}));
      }, 500);
    }
  };

  const resetRedux = () => {
    dispatch(resetPatientContext());
    dispatch(resetPatientRecordLockingContext());
  };

  const handleIOCModalCancel = () => {
    releasePatientLock();
    dispatch(setIsIOCModalOpen(false));
    clearInterval(editModalPollingInterval);
  };

  const handleIOCModalSubmit = () => {
    dispatch(setIsIOCModalOpen(false));
    clearInterval(editModalPollingInterval);
  };

  const handleOpenIOCModal = (isEdit: boolean) => {
    dispatch(setIsIOCModalOpen(true));
    setIsIOCModalEditMode(isEdit);
  };

  const handleIOCLocking = async () => {
    if (!selectedEncounter?.patientId || !selectedEncounter?.patientEncounterId) {
      return;
    }
    const patientId = selectedEncounter.patientId;
    const encounterId = selectedEncounter.patientEncounterId;
    const freshIOC = await patientService.getPatientInstanceOfCare({
      patientId,
      encounterId,
    });
    const IOC = freshIOC.entity;
    const isLocked = IOC?.isLocked || false;

    if (
        isLocked &&
        IOC?.lockedByUserId !== userName &&
        IOC?.lockedByUserId !== null &&
        IOC?.lockedByUserId?.toLowerCase() !== 'undefined'
    ) {
      setIsRequestReleaseButtonDisabled(false);
      dispatch(setLockRequestedById(IOC.lockedByUserId));
      dispatch(setInUseModalOpen_overview(true));
    } else {
      dispatch(setLockRequestedById(undefined));
      await dispatch(lockIOC({lock: true, username: userName}));
      doModalPolling(patientId, encounterId);
    }
  };

  const handleRequestReleaseOk = async () => {
    setIsRequestReleaseButtonDisabled(true);
    dispatch(
        requestReleaseOk({
          userName,
          lockablePatientRecordSection: LockablePatientRecordSection.overview,
        }),
    );
  };

  const handleRequestReleaseCancel = () => {
    resetRedux();
    dispatch(setInUseModalOpen_overview(false));
    navigate('/specialist/dashboard');
  };

  const handleReleaseRecordOk = async () => {
    dispatch(
        releaseRecordOk({
          lockablePatientRecordSection: LockablePatientRecordSection.overview,
        }),
    );
    dispatch(setIsIOCModalOpen(false));
    clearInterval(editModalPollingInterval);
  };

  const handleRequestDeniedClose = () => {
    resetRedux();
    navigate('/specialist/dashboard');
    dispatch(setRequestDeniedModalOpen_overview(false));
  };

  const handleWarmTransfer = (event: {target: {value: string}}) => {
    setIsWarmTransferCompleted(event.target.value);
    dispatch(
        updateWarmTransfer({
          isWarmTransferCompleted:
              event.target.value === 'Yes'
                  ? true
                  : event.target.value === 'No'
                      ? false
                      : null,
        }),
    );
  };

  const setInitialAccountHolderContext = async (
      contactInfo: PrimaryContact,
      isPatient: boolean,
  ) => {
    const nextAccountHolderContext = {
      userName: contactInfo.email,
      isPatient,
      firstName: contactInfo.firstName,
      lastName: contactInfo.lastName,
    };

    if (!contactInfo.email) {
      dispatch(
          setAccountHolderContext({
            ...nextAccountHolderContext,
            isExistingEmail: null,
            userId: null,
          }),
      );
      return;
    }

    const response = await userService.getAllUsers(0, 1000, {
      userName: contactInfo.email,
    });

    const {entity} = response;
    const isExistingEmail = entity.length >= 1;
    const userId = entity[0]?.userId || null;

    if (selectedEncounter.loginVerified === null) {
      if (doesChampionExist || selectedPatientChampion) {
        let getUserResponse;
        if (userId) {
          getUserResponse = await userService.getUser(userId);
        }

        if (isExistingEmail && getUserResponse?.fullUser?.cognitoStatus?.UserStatus === 'CONFIRMED') {
          const result = await userService.sendVerificationEmail(
              userId,
              selectedEncounter.patientEncounterId,
          );

          if (result.hasErrors) {
            dispatch(showErrorStatus('Email failed to send. Please resend'));
          }

          await dispatch(updateIOC({loginVerified: LoginVerifiedStatus.pending}));

          dispatch(
              setAccountHolderContext({
                ...nextAccountHolderContext,
                isExistingEmail,
                userId,
              }),
          );
          return;
        }

        await dispatch(updateIOC({loginVerified: LoginVerifiedStatus.verified}));

        dispatch(
            setAccountHolderContext({
              ...nextAccountHolderContext,
              isExistingEmail,
              userId,
            }),
        );
        return;
      }
    }

    dispatch(
        setAccountHolderContext({
          ...nextAccountHolderContext,
          isExistingEmail,
          userId,
        }),
    );
  };

  const whoIsTheAccountHolder = (
      patientChampions: PatientChampion[],
      patient: {contact?: PrimaryContact},
  ) => {
    const guarantor = patientChampions.filter((champion) => champion.isGuarantor);
    if (guarantor.length >= 1) {
      return {
        isPatient: false,
        accountHolder: guarantor[0],
      };
    }
    return {
      isPatient: true,
      accountHolder: patient,
    };
  };

  useEffect(() => {
    return () => {
      clearInterval(editModalPollingInterval);
    };
  }, []);

  useEffect(() => {
    if (
        isIOCModalOpen &&
        selectedEncounter?.patientId &&
        selectedEncounter?.patientEncounterId
    ) {
      void handleIOCLocking();
    }
    // Match IoC: lock when the edit modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIOCModalOpen]);

  useEffect(() => {
    if (
        isLockStatusReleasedOverview &&
        selectedEncounter?.patientId &&
        selectedEncounter?.patientEncounterId
    ) {
      doModalPolling(
          selectedEncounter.patientId,
          selectedEncounter.patientEncounterId,
      );
      dispatch(setLockStatusReleased_overview(false));
    }
  }, [
    dispatch,
    doModalPolling,
    isLockStatusReleasedOverview,
    selectedEncounter?.patientEncounterId,
    selectedEncounter?.patientId,
  ]);

  useEffect(() => {
    if (selectedEncounter?.isWarmTransferCompleted === true) {
      setIsWarmTransferCompleted('Yes');
    }
    if (selectedEncounter?.isWarmTransferCompleted === false) {
      setIsWarmTransferCompleted('No');
    }
    if (selectedEncounter?.isWarmTransferCompleted === null) {
      setIsWarmTransferCompleted('-1');
    }
  }, [selectedEncounter?.isWarmTransferCompleted]);

  useEffect(() => {
    if (selectedEncounter?.clientId > 0) {
      dispatch(getClientFacilities(selectedEncounter.clientId));
    }
  }, [dispatch, selectedEncounter?.clientId]);

  useEffect(() => {
    if (isLoadingEncounter) return;
    if (selectedPatientChampion === undefined) return;
    if (!doesChampionExist) return;
    if (hasRunOnce.current) return;
    if (!selectedPatient) return;

    const {accountHolder, isPatient} = whoIsTheAccountHolder(
        selectedPatientChampion,
        selectedPatient,
    );

    if (!accountHolder?.contact) return;

    void setInitialAccountHolderContext(accountHolder.contact, isPatient);
    hasRunOnce.current = true;

    return () => {
      hasRunOnce.current = false;
      dispatch(setAccountHolderContext({type: 'reset'}));
    };
    // Match IoC: initialize account holder once champions are available.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatientChampion]);

  const admissionDate = selectedEncounter?.admissionDt
      ? new Date(selectedEncounter.admissionDt)
      : '';
  const dischargeDate = selectedEncounter?.dischargeDt
      ? new Date(selectedEncounter.dischargeDt)
      : '';

  let accountHolderIcon;
  switch (selectedEncounter?.loginVerified) {
    case LoginVerifiedStatus.pending:
      accountHolderIcon = <PendingOutlined sx={{color: '#f2db00'}}/>;
      break;
    case LoginVerifiedStatus.verified:
      accountHolderIcon = <CheckCircleOutlineIcon className="green-icon"/>;
      break;
    case null:
    default:
      accountHolderIcon = <RadioButtonUncheckedIcon className="gray-icon"/>;
  }

  const workflowStatusDesc = selectedEncounter?.workflow?.workflowStatus?.workflowStatusDesc ?? '';
  const workflowSubStatusDesc = selectedEncounter?.workflow?.workflowSubStatus?.workflowSubStatusDesc;
  const workflowStatusText = `${workflowStatusDesc}${
      workflowSubStatusDesc ? ' - ' + workflowSubStatusDesc : ''
  }`;
  const workflowStatusColor = workflowSubStatusDesc === 'None'
      ? getPatientStatusByStatusId(
          selectedEncounter?.workflow?.workflowStatus?.workflowStatusId,
          StatusType['primary-status'],
          selectedEncounter?.workflow?.workflowId,
      ).color
      : getPatientStatusByStatusId(
          selectedEncounter?.workflow?.workflowSubStatus?.workflowSubStatusId,
          StatusType['sub-status'],
          selectedEncounter?.workflow?.workflowId,
      ).color;

  return (
      <>
        <CustomizeableAccordion
            icon={<FolderOpenIcon className="gray-icon"/>}
            headerText="Instance of Care"
            details={doesSelectedEncounterExist && (
                <div>
                  <Grid container justifyContent="space-between">
                    <>
                      <Grid item xs={10}/>
                      <Box justifyContent="flex-end">
                        <Grid item xs={2}>
                          <AccessControl
                              rolePageName={RolePageNames.PatientRecords}
                              actionName="Add Instance of Care"
                              renderNoAccess={() => (
                                  <IconButton disabled size="large">
                                    {' '}
                                    <EditIcon className="colorDisabled"/>
                                  </IconButton>
                              )}
                              test-id="patient-tab-edit-ioc-access"
                          >
                            <IconButton
                                onClick={() => handleOpenIOCModal(true)}
                                size="large"
                                test-id="edit-IOC-button"
                            >
                              {' '}
                              <EditIcon className="icon"/>
                            </IconButton>
                          </AccessControl>
                        </Grid>
                      </Box>
                    </>
                    <HeaderWithDetails
                        header="Provider"
                        details={selectedEncounter?.clientName}
                        width="half"
                    />
                    <HeaderWithDetails
                        header="Facility"
                        details={selectedEncounter?.facilityName}
                        width="half"
                    />
                    <HeaderWithDetails
                        header="CRM Account ID"
                        details={selectedEncounter?.clientsPatientAccountId}
                        width="half"
                    />
                    <HeaderWithDetails
                        header="CRM IOC ID"
                        details={selectedEncounter?.clientsPatientIOCId}
                        width="half"
                    />
                    <HeaderWithDetails
                        header="Billing Account ID"
                        details={selectedEncounter?.extBillingPatientAccountId}
                        testId="Billing-Account-Id"
                        width="half"
                    />
                    <HeaderWithDetails
                        header="Billing IOC ID"
                        details={selectedEncounter?.clientBillingIOCId}
                        testId="Billing-IOC-Id"
                        width="half"
                    />
                    <HeaderWithDetails
                        header="Adjusted PFR"
                        details={formatNumberToUSD(selectedEncounter?.adjustedPfrAmt)}
                        width="half"
                        testId="current-pfr-amt"
                    />
                    <HeaderWithDetails
                        header="Type of Care"
                        details={selectedEncounter?.typeOfCare?.typeOfCare}
                        width="half"
                    />
                    {selectedEncounter?.levelOfCare && (
                        <HeaderWithDetails
                            header="Level of Care"
                            details={selectedEncounter?.levelOfCare}
                            width="half"
                        />
                    )}
                    <HeaderWithDetails
                        header="Timing Risk"
                        details={selectedEncounter?.timingRisk?.timingRiskName}
                        width="half"
                    />
                    <HeaderWithDetails
                        header="Payor Risk"
                        details={selectedEncounter?.payorRisk?.payorRiskName}
                        width="half"
                    />
                    <HeaderWithDetails
                        header="Risk Class"
                        details={selectedEncounter?.riskClass?.riskClassName}
                        width="half"
                    />
                    <HeaderWithDetails
                        header="Paid to Patient"
                        details={selectedEncounter?.paidToPatient === true ? 'Yes' : 'No'}
                        width="half"
                    />
                    {admissionDate !== '' ? (
                        <HeaderWithDetails
                            header="Admission"
                            details={Utils.convertDate(admissionDate)}
                            width="half"
                        />
                    ) : (
                        <HeaderWithDetails header="Admission" width="half"/>
                    )}
                    {dischargeDate !== '' ? (
                        <HeaderWithDetails
                            header="Discharge"
                            details={Utils.convertDate(dischargeDate)}
                            width="half"
                        />
                    ) : (
                        <HeaderWithDetails header="Discharge" width="half"/>
                    )}
                    <Grid item xs={12} sm={6}>
                      <div style={{width: '80%'}}>
                        <TextField
                            select={true}
                            label="Warm Transfer Completed?"
                            name="isWarmTransferCompleted"
                            value={isWarmTransferCompleted}
                            onChange={(event: {target: {value: string}}) => {
                              handleWarmTransfer(event);
                            }}
                            onBlur={() => {}}
                            disabled={selectedEncounter?.workflow?.workflowId === 3}
                        >
                          <MenuItem value="-1">N/A</MenuItem>
                          <MenuItem value={'Yes'}>Yes</MenuItem>
                          <MenuItem value={'No'}>No</MenuItem>
                        </TextField>
                      </div>
                    </Grid>
                    <HeaderWithDetails
                        testId="ioc-workflow-status"
                        header="Workflow Status"
                        width="full"
                        details={
                          <Status
                              typographyVariant="h3"
                              text={workflowStatusText}
                              statusColor={workflowStatusColor}
                          />
                        }
                    />
                  </Grid>
                  <CustomizeableAccordion
                      icon={accountHolderIcon}
                      headerText="Account Holder Status"
                      details={
                        <AccountStatusAccordian
                            encounter={selectedEncounter}
                            firstName={accountHolderContext.firstName}
                            lastName={accountHolderContext.lastName}
                            userName={accountHolderContext.userName}
                            isPatient={accountHolderContext.isPatient}
                            userId={accountHolderContext.userId}
                        />
                      }
                  />
                </div>
            )}
        />
        {isIOCModalOpen && (
            <InstanceOfCareModal
                open={isIOCModalOpen}
                isEdit={isIOCModalEditMode}
                clientId={selectedEncounter?.clientId}
                handleModalCancel={handleIOCModalCancel}
                handleModalSubmit={handleIOCModalSubmit}
            />
        )}
        {lockingModalOpenStatus.isInUseModalOpen_overview && (
            <LockModal
                open={lockingModalOpenStatus.isInUseModalOpen_overview}
                title="Instance of Care In Use"
                subTitle1={`A release request will be sent to ${selectedEncounter?.lockedByUserId}`}
                subTitle2={`Access will be granted in ${countdownMax} seconds or less`}
                hasCountdown={false}
                countdownSeconds={countdownMax}
                primaryButtonType="primary"
                primaryButtonText="Request Release"
                handlePrimaryButton={handleRequestReleaseOk}
                secondaryButtonType="secondary"
                secondaryButtonText="Cancel"
                handleSecondaryButton={handleRequestReleaseCancel}
                icon={<LockIcon/>}
                modalType="info"
                justifyButtons="flex-end"
                primaryButtonDisabled={isRequestReleaseButtonDisabled}
            />
        )}
        {lockingModalOpenStatus.isRequestDeniedModalOpen_overview && (
            <LockModal
                open={lockingModalOpenStatus.isRequestDeniedModalOpen_overview}
                title="Request Denied"
                subTitle1={`${lockRequestedById} will retain access to this record`}
                hasCountdown={false}
                primaryButtonType="secondary"
                primaryButtonText="Close"
                handlePrimaryButton={handleRequestDeniedClose}
                icon={<LockIcon/>}
                modalType="error"
                justifyButtons="flex-end"
            />
        )}
        {lockingModalOpenStatus.isLockRequestedModalOpen_overview && (
            <LockModal
                open={lockingModalOpenStatus.isLockRequestedModalOpen_overview}
                title="Instance of Care Requested"
                subTitle1={`${lockRequestedById} requested this record`}
                subTitle2={`Access will be granted in ${countdownMax} seconds or less`}
                hasCountdown
                countdownSeconds={countdownMax}
                primaryButtonType="primary"
                primaryButtonText="Release Record"
                handlePrimaryButton={handleReleaseRecordOk}
                secondaryButtonType="danger"
                secondaryButtonText="Deny Request"
                handleSecondaryButton={() =>
                  dispatch(
                      denyRequestOk({
                        lockablePatientRecordSection: LockablePatientRecordSection.overview,
                      }),
                  )
                }
                icon={<WarningRoundedIcon/>}
                modalType="warning"
                justifyButtons="space-between"
            />
        )}
      </>
  );
};
